import { Box } from '@mui/material';
import { TruncateText } from 'components/truncate/TruncateText';
import { toLocalDateTimeString } from 'lib';

export type ColumnDef = {
    field: string;
    headerName: string;
    width?: number;
    flex?: number;
    resizable?: boolean;
    sortable?: boolean;
    filterable?: boolean;
    renderCell?: (params: any) => React.JSX.Element;
    valueGetter?: any;
};

export const getUserWalletName = (userWallets: any[], value: any): string => {
    const filteredWallet = userWallets.find((wallet) => wallet.id === value);
    return filteredWallet
        ? filteredWallet.fullName || filteredWallet.email
        : '';
};

export const getUserWalletOrSystemWalletName = (
    userWallets: any[],
    systemWallets: any[],
    value: any
): string => {
    let combinedWalletData = [...userWallets, ...systemWallets];
    const filteredWallet = combinedWalletData.find(
        (wallet) => wallet.id === value
    );
    return filteredWallet
        ? filteredWallet.fullName ||
              filteredWallet.email ||
              filteredWallet.description
        : '';
};

const truncateTextWithEllipsisIcon = (value: string): string => {
    return value.length > 20 ? value.slice(0, 20) + '...' : value;
};

const renderCellForSmartContract = (params: any): string => {
    return truncateTextWithEllipsisIcon(params.value);
};

const renderCellForConsentContract = (params: any): string => {
    return params.value;
};

export const getReceiptColumns = (
    isSmartContract: boolean,
    userWallets: any[],
    systemWallets: any[]
): ColumnDef[] => {
    const columns = [
        {
            field: 'id',
            headerName: 'SDOID',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: isSmartContract
                ? renderCellForSmartContract
                : renderCellForConsentContract,
        },
        {
            field: 'timestamp',
            headerName: 'Last Modified',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: (params: any): any => {
                return toLocalDateTimeString(params.value, true);
            },
        },
        {
            field: 'contractOwnerId',
            headerName: 'Contract Owner',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: (params: any): any => {
                const walletName = getUserWalletOrSystemWalletName(
                    userWallets,
                    systemWallets,
                    params.value
                );
                if (!walletName) {
                    return <></>;
                }
                return (
                    <Box sx={{ wordBreak: 'break-all' }}>
                        <TruncateText value={walletName} numOflines={4} />
                    </Box>
                );
            },
        },
        {
            field: 'effectiveId',
            headerName: 'EffectiveID',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: (params: any): any => {
                const walletName = getUserWalletOrSystemWalletName(
                    userWallets,
                    systemWallets,
                    params.value
                );
                if (!walletName) {
                    return <></>;
                }
                return isSmartContract ? (
                    truncateTextWithEllipsisIcon(walletName)
                ) : (
                    <TruncateText value={walletName} numOflines={1} />
                );
            },
        },
        {
            field: 'loggedInId',
            headerName: 'LoggedInID',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: (params: any): any => {
                const walletName = getUserWalletName(userWallets, params.value);
                if (!walletName) {
                    return <></>;
                }
                return isSmartContract
                    ? truncateTextWithEllipsisIcon(walletName)
                    : walletName;
            },
        },
    ];

    if (isSmartContract) {
        columns.push({
            field: 'function',
            headerName: 'Function',
            flex: 1,
            width: 150,
            resizable: false,
            renderCell: renderCellForSmartContract,
        });
    }

    return columns;
};
